import React, { useState, useEffect } from 'react';
import supabase from '../../lib/supabaseClient';

// Verifies both email and phone OTP from Supabase/Twilio and saves mover application
const FinalOTPVerification = ({ data, onVerified, onBack }) => {
  const [emailCode, setEmailCode] = useState('');
  const [phoneCode, setPhoneCode] = useState('');
  const [verifying, setVerifying] = useState(false);
  const [error, setError] = useState('');
  const [emailOtpSent, setEmailOtpSent] = useState(false);
  const [phoneOtpSent, setPhoneOtpSent] = useState(false);
  const [sendingEmail, setSendingEmail] = useState(false);
  const [sendingPhone, setSendingPhone] = useState(false);
  const [emailTimer, setEmailTimer] = useState(0);
  const [phoneTimer, setPhoneTimer] = useState(0);

  // Timer countdown for email
  useEffect(() => {
    if (emailTimer > 0) {
      const interval = setInterval(() => {
        setEmailTimer(prev => prev - 1);
      }, 1000);
      return () => clearInterval(interval);
    }
  }, [emailTimer]);

  // Timer countdown for phone
  useEffect(() => {
    if (phoneTimer > 0) {
      const interval = setInterval(() => {
        setPhoneTimer(prev => prev - 1);
      }, 1000);
      return () => clearInterval(interval);
    }
  }, [phoneTimer]);

  const requestEmailOTP = async () => {
    setSendingEmail(true);
    setError('');

    try {
      // Use signInWithOtp for email - more reliable for OTP delivery
      const { data: otpData, error: otpError } = await supabase.auth.signInWithOtp({
        email: data.email,
        options: {
          shouldCreateUser: true,
          data: {
            phone: data.phone,
            company_name: data.companyName,
            user_type: 'mover'
          }
        }
      });


      if (otpError) {
        throw otpError;
      }

      setEmailOtpSent(true);
      setEmailTimer(60); // 60 second cooldown
    } catch (err) {
      setError(err.message || 'Failed to send email OTP. Please check your email address and try again.');
    } finally {
      setSendingEmail(false);
    }
  };

  const requestPhoneOTP = async () => {
    setSendingPhone(true);
    setError('');

    try {
      // Send phone OTP using signInWithOtp
      const { error: phoneError } = await supabase.auth.signInWithOtp({
        phone: data.phone,
        options: {
          channel: 'sms'
        }
      });

      if (phoneError) throw phoneError;

      setPhoneOtpSent(true);
      setPhoneTimer(60); // 60 second cooldown
    } catch (err) {
      setError(err.message || 'Failed to send phone OTP. Please try again.');
    } finally {
      setSendingPhone(false);
    }
  };

  const handleVerify = async () => {
    if (emailCode.length !== 6 || phoneCode.length !== 6) return;

    setVerifying(true);
    setError('');

    try {
      // Step 1: Verify email OTP (using 'email' type since we used signInWithOtp)
      const { data: emailVerifyData, error: emailVerifyError } = await supabase.auth.verifyOtp({
        email: data.email,
        token: emailCode,
        type: 'email'
      });


      if (emailVerifyError) {
        throw new Error('Invalid email verification code: ' + emailVerifyError.message);
      }

      // After email verification, we need to set the password
      const { error: passwordError } = await supabase.auth.updateUser({
        password: data.password
      });

      if (passwordError) {
        throw new Error('Failed to set password: ' + passwordError.message);
      }

      // Step 2: Verify phone OTP
      const { error: phoneVerifyError } = await supabase.auth.verifyOtp({
        phone: data.phone,
        token: phoneCode,
        type: 'sms'
      });

      if (phoneVerifyError) {
        throw new Error('Invalid phone verification code: ' + phoneVerifyError.message);
      }

      // Step 3: Get current user to link application
      const { data: { user } } = await supabase.auth.getUser();

      // Step 4: Save mover application to database
      const { data: applicationData, error: dbError } = await supabase
        .from('movers_application')
        .insert([{
          email: data.email,
          phone: data.phone,
          company_name: data.companyName,
          website_link: data.website || null,
          business_address: data.businessAddress || null,
          city: data.city || null,
          state: data.state || null,
          zip_code: data.zipCode || null,
          years_in_business: data.yearsInBusiness ? parseInt(data.yearsInBusiness) : null,
          number_of_employees: data.employeeCount ? parseInt(data.employeeCount) : null,
          google_profile_link: data.googleMapsProfile || null,
          company_description: data.description || null,
          status: 'Pending'
        }])
        .select()
        .single();

      if (dbError) {
        throw dbError;
      }


      // Also add to local store for backwards compatibility
      try {
        const store = await import('../../services/moverApplicationsStore');
        store.default.initMoverApplications();
        store.default.addMoverApplication({
          companyName: data.companyName,
          email: data.email,
          phone: data.phone,
          city: data.city,
          state: data.state,
          employeeCount: parseInt(data.employeeCount || '0', 10),
          description: data.description,
          businessLicense: 'Pending',
          insurance: 'Pending'
        });
      } catch (e) {
      }

      // Call onVerified to navigate to success page
      onVerified();
    } catch (err) {
      setError(err.message || 'Verification failed. Please check your codes and try again.');
    } finally {
      setVerifying(false);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-semibold text-gray-800 mb-2">Verify Email & Phone</h2>
        <p className="text-sm text-gray-600">
          Request verification codes for your email and phone number, then enter them below.
        </p>
      </div>

      {/* Info Banner */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-start">
          <svg className="w-5 h-5 text-blue-600 mt-0.5 mr-3 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
          </svg>
          <div className="text-sm text-blue-800">
            <p className="font-medium mb-1">Click "Request OTP" to receive verification codes</p>
            <p className="text-xs">
              You'll receive a 6-digit code at <strong>{data.email}</strong> and <strong>{data.phone}</strong>.
            </p>
          </div>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-start">
            <svg className="w-5 h-5 text-red-600 mt-0.5 mr-3 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
            </svg>
            <p className="text-sm text-red-700">{error}</p>
          </div>
        </div>
      )}

      {/* Email and Phone OTP Inputs */}
      <div className="grid md:grid-cols-2 gap-6">
        {/* Email OTP */}
        <div className="bg-gray-50 rounded-lg p-6 border-2 border-gray-200">
          <div className="text-center space-y-4">
            <div className="flex items-center justify-center gap-2 text-gray-700 mb-4">
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-5 h-5 text-blue-600"><rect width="20" height="16" x="2" y="4" rx="2" /><path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7" /></svg>
              <span className="font-medium text-sm text-blue-700">Email Verification</span>
            </div>

            <p className="text-xs text-gray-600 break-all">{data.email}</p>

            <button
              onClick={requestEmailOTP}
              disabled={sendingEmail || emailTimer > 0}
              className="w-full bg-gradient-to-r from-blue-500 to-blue-600 text-white py-3 rounded-lg font-semibold hover:from-blue-600 hover:to-blue-700 transition disabled:opacity-50 disabled:cursor-not-allowed shadow-md"
            >
              {sendingEmail ? (
                <div className="flex items-center justify-center gap-2">
                  <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent"></div>
                  <span>Sending...</span>
                </div>
              ) : emailTimer > 0 ? (
                `Request OTP (${emailTimer}s)`
              ) : emailOtpSent ? (
                'Resend Email OTP'
              ) : (
                'Request Email OTP'
              )}
            </button>

            {emailOtpSent && (
              <>
                <input
                  value={emailCode}
                  onChange={(e) => setEmailCode(e.target.value.replace(/\D/g, ''))}
                  maxLength={6}
                  placeholder="000000"
                  className="w-full px-6 py-4 border-2 border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-center tracking-[0.5em] text-2xl font-bold"
                />
                <p className="text-xs text-gray-500">Enter the 6-digit code from your email</p>
              </>
            )}
          </div>
        </div>

        {/* Phone OTP */}
        <div className="bg-gray-50 rounded-lg p-6 border-2 border-gray-200">
          <div className="text-center space-y-4">
            <div className="flex items-center justify-center gap-2 text-gray-700 mb-4">
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-5 h-5 text-green-600"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" /></svg>
              <span className="font-medium text-sm text-green-700">Phone Verification</span>
            </div>

            <p className="text-xs text-gray-600">{data.phone}</p>

            <button
              onClick={requestPhoneOTP}
              disabled={sendingPhone || phoneTimer > 0}
              className="w-full bg-gradient-to-r from-green-500 to-green-600 text-white py-3 rounded-lg font-semibold hover:from-green-600 hover:to-green-700 transition disabled:opacity-50 disabled:cursor-not-allowed shadow-md"
            >
              {sendingPhone ? (
                <div className="flex items-center justify-center gap-2">
                  <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent"></div>
                  <span>Sending...</span>
                </div>
              ) : phoneTimer > 0 ? (
                `Request OTP (${phoneTimer}s)`
              ) : phoneOtpSent ? (
                'Resend Phone OTP'
              ) : (
                'Request Phone OTP'
              )}
            </button>

            {phoneOtpSent && (
              <>
                <input
                  value={phoneCode}
                  onChange={(e) => setPhoneCode(e.target.value.replace(/\D/g, ''))}
                  maxLength={6}
                  placeholder="000000"
                  className="w-full px-6 py-4 border-2 border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500 text-center tracking-[0.5em] text-2xl font-bold"
                />
                <p className="text-xs text-gray-500">Enter the 6-digit code from SMS</p>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Verify Button - No Back Button */}
      <div className="pt-2">
        <button
          onClick={handleVerify}
          disabled={emailCode.length !== 6 || phoneCode.length !== 6 || verifying}
          className="w-full bg-gradient-to-r from-orange-500 to-orange-600 text-white py-4 rounded-lg font-semibold hover:from-orange-600 hover:to-orange-700 transition disabled:opacity-50 disabled:cursor-not-allowed shadow-lg text-lg"
        >
          {verifying ? (
            <div className="flex items-center justify-center gap-2">
              <div className="animate-spin rounded-full h-6 w-6 border-2 border-white border-t-transparent"></div>
              <span>Verifying & Submitting Application...</span>
            </div>
          ) : (
            'Verify & Submit Application'
          )}
        </button>
      </div>
    </div>
  );
};

export default FinalOTPVerification;
